import React, { useContext, useState, useMemo, useCallback, memo } from "react";
import { useSelector } from "react-redux";
import { Modal, ModalBody } from "reactstrap";
import { DF_OT0019Context } from "../context";
import TranspaleStringToComponente from "../../../../components/Dynamic/TranspaleStringToComponente";
import BizinBackend from "../../../../helpers/bizin_helper";
import useGetCsrfToken from "../../../../Hooks/useGetCsrfToken";

const IDIOMA_PADRAO = "pt-BR";
const ROTA_VOLTAR = "/DF_OT0016";
const PROC_SALVAR = "FRONT_Workspace_DF_OT0019_Salvar_Categoria";
const INITIAL_FILTER_STATE = [{}];

function GridBody() {
  const { userProfile, navigate } = useContext(DF_OT0019Context);
  const { access_token } = useSelector((state) => state.login.user);
  const csrfToken = useGetCsrfToken();

  const [preFilter, setPreFilter] = useState(INITIAL_FILTER_STATE);
  const [screenMode, setScreenMode] = useState("normal");
  const [loading, setLoading] = useState(false);

  const userId = userProfile?.ID_Usuario ?? 0;

  const getFilterValue = useCallback(
    (key) => {
      const item = preFilter[0]?.[key];
      if (item === undefined || item === null) return "";
      return typeof item === "object" && "value" in item ? item.value : item;
    },
    [preFilter],
  );

  const idOrigem = getFilterValue("Filtro_ID_Empresa_Origem");
  const idDestino = getFilterValue("Filtro_ID_Empresa_Destino");
  const idCategoria = getFilterValue("Filtro_ID_Categoria");
  const hasOrigem = Boolean(idOrigem);

  const handleFilterChange = useCallback((filters) => {
    setPreFilter((state) => {
      const currentState = state[0] || {};
      const isOrigemChanging =
        "Filtro_ID_Empresa_Origem" in filters &&
        filters.Filtro_ID_Empresa_Origem !==
          currentState.Filtro_ID_Empresa_Origem;

      return [
        {
          ...currentState,
          ...filters,
          ...(isOrigemChanging && { Filtro_ID_Categoria: "" }),
        },
      ];
    });
  }, []);

  const handleCancel = useCallback(() => {
    navigate?.(ROTA_VOLTAR);
  }, [navigate]);

  const handleSave = () => {
    setScreenMode("confirm");
  };

  const handleConfirm = async () => {
    try {
      setLoading(true);
      const payload = {
        1: userId,
        2: idOrigem || null,
        3: idDestino || null,
        4: idCategoria || null,
      };

      const response = await BizinBackend.executeProcedure(
        payload,
        PROC_SALVAR,
        csrfToken,
        access_token,
      );

      if (
        response?.statusCode === 500 ||
        response?.Status === "ERROR" ||
        response?.error
      ) {
        setScreenMode("error");
        return;
      }

      setScreenMode("success");
    } catch (error) {
      console.error("Erro ao cadastrar categoria:", error);
      setScreenMode("error");
    } finally {
      setLoading(false);
    }
  };

  const empresaOrigemProps = useMemo(
    () => ({
      value: "",
      type: "Combobox",
      title: "Empresa Origem",
      id: "Filtro_ID_Empresa_Origem",
      isDisabled: false,
      procedure: "SRM_Agendamento_Get_Empresas",
      procedureParams: [userId],
    }),
    [userId],
  );

  const empresaDestinoProps = useMemo(
    () => ({
      value: "",
      type: "Combobox",
      title: "Empresa Destino",
      id: "Filtro_ID_Empresa_Destino",
      isDisabled: false,
      procedure: "FRONT_Componente_Combo_Empresas",
      procedureParams: [String(userId), IDIOMA_PADRAO, "ORIGEM", "NF"],
    }),
    [userId],
  );

  const categoriaProps = useMemo(
    () => ({
      value: "",
      type: "Combobox",
      title: "Categoria",
      id: "Filtro_ID_Categoria",
      isDisabled: !hasOrigem,
      procedure: hasOrigem ? "FRONT_Workspace_DF_OT0016_Categorias" : "",
      procedureParams: [idOrigem],
      placeholder: hasOrigem
        ? "Selecione a categoria"
        : "Selecione a empresa origem",
    }),
    [hasOrigem, idOrigem],
  );

  return (
    <>
      <div className="bg-white mb-3 card-content shadow-card p-3">
        <div className="row align-items-end gx-3">
          <div className="col-lg-3 col-md-4 col-12">
            <TranspaleStringToComponente
              elementId="Filtro_ID_Empresa_Origem"
              elementProps={empresaOrigemProps}
              preFilter={preFilter}
              onPreFilter={handleFilterChange}
            />
          </div>

          <div className="col-lg-3 col-md-4 col-12">
            <TranspaleStringToComponente
              elementId="Filtro_ID_Empresa_Destino"
              elementProps={empresaDestinoProps}
              preFilter={preFilter}
              onPreFilter={handleFilterChange}
            />
          </div>

          <div className="col">
            <TranspaleStringToComponente
              key={`categoria-${idOrigem}`}
              elementId="Filtro_ID_Categoria"
              elementProps={categoriaProps}
              preFilter={preFilter}
              onPreFilter={handleFilterChange}
            />
          </div>

          <div className="col-auto d-flex justify-content-end align-items-center gap-2 mb-3">
            <button
              type="button"
              className="btn btn-primary-dark text-white fw-semibold"
              onClick={handleSave}
            >
              Gravar
            </button>
          </div>
        </div>
      </div>

      <div className="mt-3">
        <button
          type="button"
          className="btn bg-white border border-color-cinza2 text-color-azul1 fw-bold px-4 text-color-azul4-hover"
          onClick={handleCancel}
        >
          Voltar
        </button>
      </div>

      <Modal
        isOpen={screenMode !== "normal"}
        centered
        toggle={() => setScreenMode("normal")}
      >
        <ModalBody className="p-4">
          {screenMode === "confirm" && (
            <div>
              <div className="text-center">
                <div className="icon-warning mb-4">
                  <i className="fas fa-exclamation-triangle"></i>
                </div>

                <h5 className="modal-title-custom mb-3">
                  Deseja cadastrar esta
                  <br />
                  categoria?
                </h5>

                <p className="modal-description mb-4">
                  Ao clicar em 'Confirmar' a categoria será
                  <br />
                  cadastrada.
                </p>

                <div className="d-flex justify-content-center gap-3">
                  <button
                    type="button"
                    className="btn border-0 fw-bold px-4 btn-cancelar-co0005"
                    onClick={() => setScreenMode("normal")}
                    disabled={loading}
                  >
                    Cancelar
                  </button>

                  <button
                    type="button"
                    className="btn border-0 fw-bold px-4 btn-confirmar-co0005"
                    onClick={handleConfirm}
                    disabled={loading}
                  >
                    {loading ? "Processando..." : "Confirmar"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {screenMode === "success" && (
            <div>
              <div className="text-center">
                <div className="icon-success mb-4">
                  <i className="fas fa-check"></i>
                </div>

                <h5 className="modal-title-custom mb-3">Sucesso!</h5>

                <p className="modal-subtext mb-4">
                  Operação realizada com sucesso!
                </p>

                <button
                  type="button"
                  className="btn border-0 fw-bold px-4 btn-confirmar-co0005"
                  onClick={handleCancel}
                >
                  Ok!
                </button>
              </div>
            </div>
          )}

          {screenMode === "error" && (
            <div>
              <div className="text-center">
                <div className="icon-error mb-4">
                  <i className="fas fa-times"></i>
                </div>

                <h5 className="modal-title-custom mb-4">
                  Erro em cadastrar a categoria
                </h5>

                <p className="modal-description mb-4">
                  Repita o processo novamente para concluir o
                  <br />
                  cadastramento com sucesso.
                </p>

                <div className="d-flex justify-content-center">
                  <button
                    type="button"
                    className="btn border-0 fw-bold px-4 btn-confirmar-co0005"
                    onClick={() => setScreenMode("confirm")}
                  >
                    Ok!
                  </button>
                </div>
              </div>
            </div>
          )}
        </ModalBody>
      </Modal>
    </>
  );
}

export default memo(GridBody);
